import constants from '../constants';
import { CreateViewStateFn, DataType } from '../types';
import { CreatePageView, useCreatePageView } from './usePageViews';
import { buildSavedViewLink } from './utils';

const { DIRECT_LINK_PURPOSE } = constants;

export type CreateLinkOptions = { dataType?: DataType; purpose?: string; pathname?: string; search?: string };
export type CreateLinkResult = {
  url: string;
  viewKey: string;
};

export function useCreateDirectLink<ViewState extends object>(): {
  createLink: (createViewState: CreateViewStateFn<ViewState>, options?: CreateLinkOptions) => Promise<CreateLinkResult>;
} {
  const { execute } = useCreatePageView();

  async function createLink(
    createViewState: CreateViewStateFn<ViewState>,
    { dataType = 'static', purpose = DIRECT_LINK_PURPOSE, pathname, search }: CreateLinkOptions = {}
  ): Promise<CreateLinkResult> {
    const viewState = createViewState(dataType);
    if (!viewState) {
      throw new Error('View state is invalid');
    }
    const linkMeta = { pathname: pathname ?? window.location.pathname, search: search ?? window.location.search };

    const pageView: CreatePageView<ViewState> = {
      data: viewState,
      dataType,
      meta: {
        purpose,
        ...(purpose === DIRECT_LINK_PURPOSE && linkMeta),
      },
    };

    const viewKey = await execute(pageView);

    if (!viewKey) {
      throw Error('Failed to save view');
    }

    const url = buildSavedViewLink(viewKey, linkMeta);
    return { viewKey, url };
  }

  return {
    createLink,
  };
}
